import React, { Fragment, useState, useEffect, useCallback } from "react";
import { Row, Col, Card, CardBody, CardHeader, Button, Input, Form } from "reactstrap";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import NoDataFound from "../../Components/Common/NoDataFound";
import { customStyles } from "../../Constant";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import { copyTextToClipboard } from "../../Components/Common/CommonFunction";
import { FaEye, FaPaperPlane, FaCopy } from "react-icons/fa";

const PAGE_SIZES = [10, 25, 50, 100];

const InvitationCampaignList = () => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [sendingId, setSendingId] = useState(null);

    const fetchCampaigns = useCallback(async (page, perPageSize, search, sortBy, sortDir) => {
        setLoading(true);
        try {
            const res = await AxiosInstance().post("/admin/invitation_campaign/list", {
                page,
                per_page: perPageSize,
                search: (search || "").trim() || undefined,
                sort_by: sortBy || "created_at",
                sort_dir: sortDir || "desc",
            });
            setCampaigns(Array.isArray(res.data?.data) ? res.data.data : []);
            setTotalRows(typeof res.data?.total === "number" ? res.data.total : 0);
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.message || "Failed to load campaigns");
            setCampaigns([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCampaigns(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [currentPage, perPage, searchApplied, sortColumn, sortDirection, fetchCampaigns]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            const d = new Date(dateStr);
            return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setSearchApplied(searchQuery);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };
    const handleSort = (selectedColumn, direction) => {
        const field = selectedColumn?.id || "created_at";
        setSortColumn(field);
        setSortDirection(direction === "asc" ? "asc" : "desc");
        setCurrentPage(1);
    };

    const handleSendCampaign = async (campaignId) => {
        const result = await Swal.fire({
            title: "Send campaign?",
            text: "This will send emails to all PENDING recipients.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, send",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#0d6efd",
        });
        if (!result.isConfirmed) return;
        setSendingId(campaignId);
        try {
            const res = await AxiosInstance().post(`/admin/invitation_campaign/send/${campaignId}`);
            if (res.data.status) {
                toast.success(res.data.message || "Campaign sent");
                fetchCampaigns(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Send failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Send failed");
        } finally {
            setSendingId(null);
        }
    };

    const campaignShareUrl = (publicToken) => {
        const base = (process.env.PUBLIC_URL || "/job").replace(/\/$/, "");
        return `${window.location.origin}${base}/public/recruiter-campaign/${publicToken}`;
    };

    const handleCopyCampaignLink = async (row) => {
        const token = (row.public_invite_token || "").trim();
        if (!token) {
            toast.error("No share token for this campaign yet. Refresh the list and try again.");
            return;
        }
        const url = campaignShareUrl(token);
        const ok = await copyTextToClipboard(url);
        if (ok) {
            toast.success("Campaign link copied. Share it in Gmail or any email client.");
        } else {
            toast.error("Could not copy. Copy manually: " + url);
        }
    };

    const tableColumns = [
        { name: "#", cell: (row, index) => (currentPage - 1) * perPage + index + 1, width: "50px", sortable: false },
        { id: "campaign_name", name: "Campaign Name", selector: (row) => row.campaign_name || "—", sortable: true, grow: 1.5 },
        { id: "email_subject", name: "Email Subject", selector: (row) => row.email_subject || "—", sortable: true, grow: 1.5 },
        { id: "total_recipients", name: "Total Recipients", selector: (row) => row.total_recipients ?? "—", width: "120px", center: true },
        {
            id: "status",
            name: "Status",
            selector: (row) => row.status || "—",
            width: "120px",
            cell: (row) => <span className="badge bg-secondary">{row.status || "—"}</span>,
        },
        { id: "created_at", name: "Created", selector: (row) => row.created_at || "—", sortable: true, width: "110px", cell: (row) => <span className="text-muted small">{formatDate(row.created_at)}</span> },
        { id: "scheduled_at", name: "Scheduled", selector: (row) => row.scheduled_at || "—", width: "110px", cell: (row) => <span className="text-muted small">{formatDate(row.scheduled_at)}</span> },
        {
            name: "Actions",
            width: "300px",
            cell: (row) => (
                <div className="d-flex gap-1 flex-wrap">
                    <Button color="info" size="sm" onClick={() => navigate(`/job/admin/invitation-campaign/${row.campaign_id}`)} title="View">
                        <FaEye />
                    </Button>
                    <Button color="primary" size="sm" onClick={() => handleSendCampaign(row.campaign_id)} disabled={sendingId === row.campaign_id} title="Send">
                        <FaPaperPlane />
                    </Button>
                    <Button color="secondary" size="sm" onClick={() => handleCopyCampaignLink(row)} title="Copy link">
                        <FaCopy />
                    </Button>
                </div>
            ),
        },
    ];

    if (loading && campaigns.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Invitation Campaigns" mainTitle="Admin" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner />
                </div>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Invitation Campaigns" mainTitle="Admin" />
            <Row>
                <Col xs="12" className="box-col-12 xl-100">
                    <Card>
                        <CardHeader className="py-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                                <Form className="theme-form me-2 flex-grow-1" onSubmit={handleSearchSubmit}>
                                    <div className="input-group flex-nowrap" style={{ height: 35, maxWidth: "350px" }}>
                                        <Input
                                            className="form-control"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            type="search"
                                            placeholder="Search campaigns..."
                                        />
                                        <Button color="primary" type="submit" className="btn-primary input-group-text pe-3 ps-3">Search</Button>
                                    </div>
                                </Form>
                                <Button color="primary" onClick={() => navigate("/job/admin/invitation-campaign/new")}>
                                    <i className="fa fa-plus me-1" /> Create Campaign
                                </Button>
                            </div>
                        </CardHeader>
                        <CardBody className="px-0 py-3">
                            <DataTable
                                responsive
                                customStyles={customStyles}
                                data={campaigns}
                                columns={tableColumns}
                                striped
                                pagination
                                paginationServer
                                sortServer
                                onSort={handleSort}
                                defaultSortFieldId="created_at"
                                defaultSortAsc={false}
                                paginationTotalRows={totalRows}
                                paginationPerPage={perPage}
                                paginationRowsPerPageOptions={PAGE_SIZES}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerRowsChange}
                                progressPending={loading}
                                progressComponent={<Spinner />}
                                noDataComponent={<NoDataFound message="No campaigns found" />}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <ToastContainer />
        </Fragment>
    );
};

export default InvitationCampaignList;
